import { Core } from "@faire/design-tokens";
import { Close } from "@faire/web--source/slate/icons/foundation/Close";
import { Input } from "@faire/web--source/slate/Input";
import { isMatch as isDateMatch } from "date-fns/isMatch";
import { parse as parseDate } from "date-fns/parse";
import React, { useState, useCallback, ChangeEvent } from "react";
import { NumberFormatBase } from "react-number-format";
import styled from "styled-components";

interface USDateInputProps {
  value?: string;
  onChange: (value: string | undefined) => void;
  onBlur?: (value: string) => void;
  placeholder?: string;
  name: string;
  error?: string;
}

export const AdditionalInfoUSDateInput: React.FC<USDateInputProps> = ({
  value,
  onChange,
  onBlur,
  placeholder = "mm/dd/yyyy",
  name,
  ...props
}) => {
  const [internalValue, setInternalValue] = useState(value);

  const format = useCallback((val: string) => {
    if (!val) {
      return "";
    }

    const month = formatMonth(val.substring(0, 2));
    const day = formatDay(val.substring(2, 4));
    const year = val.substring(4, 8);

    return `${month}/${day || "  "}/${year}`;
  }, []);

  const handleInternalValueChange = useCallback(
    (value: string) => {
      if (value && value.length === 10 && isDateMatch(value, "P")) {
        const dateValue = parseDate(value, "P", new Date()).getTime();
        onChange(dateValue.toString());
      } else {
        onChange(undefined);
      }
    },
    [onChange]
  );

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInternalValue(newValue);
      handleInternalValueChange(newValue);
    },
    [handleInternalValueChange]
  );

  const handleBlur = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      handleInternalValueChange(value);
      onBlur?.(value);
    },
    [handleInternalValueChange, onBlur]
  );

  const handleClear = useCallback(() => {
    setInternalValue("");
    onChange(undefined);
  }, [onChange]);

  return (
    <NumberFormatBase
      {...props}
      name={name}
      format={format}
      type="text"
      displayType="input"
      inputMode="numeric"
      customInput={Input}
      value={internalValue}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder={placeholder}
      suffixNode={
        internalValue ? (
          <IconButton onClick={handleClear}>
            <Close width="12px" color={Core.icon.subdued} />
          </IconButton>
        ) : null
      }
    />
  );
};

const formatMonth = (month: string) => {
  if (month.length === 1 && Number(month[0]) > 1) {
    return `0${month[0]}`;
  } else if (month.length === 2) {
    if (Number(month) === 0) {
      return "01";
    } else if (Number(month) > 12) {
      return "12";
    }
  }
  return month;
};

const formatDay = (day: string) => {
  if (day.length === 1 && Number(day[0]) > 3) {
    return `0${day[0]}`;
  } else if (day.length === 2) {
    if (Number(day) === 0) {
      return "01";
    } else if (Number(day) > 31) {
      return "31";
    }
  }
  return day;
};

const IconButton = styled.button.attrs({ type: "button" })`
  display: flex;
  width: 12px;
  height: 12px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
`;
